import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLocalization } from '../../contexts/LocalizationContext';
import userService from '../../services/userService';
import Loading from '../../components/Common/Loading';
import Button from '../../components/Common/Button';
import Table from '../../components/Common/Table';
import EditUserModal from './EditUserModal';

const ROLE_LABELS = {
  1: { en: 'Employee',        ar: 'موظف',          badge: 'badge-neutral' },
  2: { en: 'Order Manager',   ar: 'مدير الطلبات',  badge: 'badge-info' },
  3: { en: 'Product Manager', ar: 'مدير المنتجات', badge: 'badge-warning' },
  4: { en: 'Super Admin',     ar: 'مدير عام',      badge: 'badge-danger' },
};

const UsersIndex = () => {
  const { get, isRtl } = useLocalization();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [editingUser, setEditingUser] = useState(null);

  useEffect(() => { loadUsers(); }, []);

  const loadUsers = async () => {
    try {
      setIsLoading(true);
      const data = await userService.getAll();
      setUsers(data);
    } catch (error) {
      console.error('Error loading users:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const filteredUsers = users.filter(user => {
    if (!searchTerm) return true;
    const s = searchTerm.toLowerCase();
    return (
      user.username?.toLowerCase().includes(s) ||
      user.fullName?.toLowerCase().includes(s) ||
      user.email?.toLowerCase().includes(s)
    );
  });

  const columns = [
    { header: isRtl ? 'اسم المستخدم' : 'Username', accessor: 'username' },
    { header: isRtl ? 'الاسم الكامل' : 'Full Name', accessor: 'fullName' },
    { header: isRtl ? 'البريد الإلكتروني' : 'Email', accessor: 'email' },
    {
      header: isRtl ? 'الدور' : 'Role',
      accessor: 'role',
      render: (value) => {
        const r = ROLE_LABELS[value] || ROLE_LABELS[1];
        return <span className={`badge ${r.badge}`}>{isRtl ? r.ar : r.en}</span>;
      },
    },
    {
      header: isRtl ? 'الحالة' : 'Status',
      accessor: 'isActive',
      render: (value) => (
        <span className={`badge ${value ? 'badge-success' : 'badge-danger'}`}>
          {value ? (isRtl ? 'نشط' : 'Active') : (isRtl ? 'معطل' : 'Inactive')}
        </span>
      ),
    },
    {
      header: isRtl ? 'إجراءات' : 'Actions',
      accessor: 'id',
      render: (_, row) => (
        <Button size="small" variant="secondary" onClick={() => setEditingUser(row)}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
          </svg>
          {isRtl ? 'تعديل' : 'Edit'}
        </Button>
      ),
    },
  ];

  if (isLoading) {
    return <Loading text={isRtl ? 'جاري التحميل...' : 'Loading users...'} />;
  }

  return (
    <div className="users-page">
      <div className="page-header">
        <div className="page-header-content">
          <div className="page-header-main">
            <h1 className="page-title">{isRtl ? 'مستخدمي الإدارة' : 'Admin Users'}</h1>
            <p className="page-description">{isRtl ? 'إدارة مسؤولي النظام ومستويات الوصول' : 'Manage system administrators and their access levels'}</p>
          </div>
          <div className="page-header-actions">
            <Button variant="secondary" onClick={loadUsers} disabled={isLoading}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="23 4 23 10 17 10"></polyline>
                <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"></path>
              </svg>
              <span className="hidden-mobile">{isRtl ? 'تحديث' : 'Refresh'}</span>
            </Button>
            <Button variant="primary" onClick={() => navigate('/admin/users/create')}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <line x1="12" y1="5" x2="12" y2="19"></line>
                <line x1="5" y1="12" x2="19" y2="12"></line>
              </svg>
              {get('add_new_user')}
            </Button>
          </div>
        </div>
      </div>

      <div className="stats-grid mb-6">
        <div className="stat-card">
          <div className="stat-card-content">
            <div className="stat-icon stat-icon-info">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                <circle cx="9" cy="7" r="4"></circle>
                <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
              </svg>
            </div>
            <div className="stat-details">
              <div className="stat-number">{users.length}</div>
              <div className="stat-label">{isRtl ? 'إجمالي المستخدمين' : 'Total Users'}</div>
            </div>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-card-content">
            <div className="stat-icon stat-icon-success">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                <polyline points="22 4 12 14.01 9 11.01"></polyline>
              </svg>
            </div>
            <div className="stat-details">
              <div className="stat-number">{users.filter(u => u.isActive).length}</div>
              <div className="stat-label">{isRtl ? 'مستخدمون نشطون' : 'Active Users'}</div>
            </div>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <div className="search-box">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="11" cy="11" r="8"></circle>
              <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
            </svg>
            <input
              type="text"
              placeholder={isRtl ? 'بحث عن المستخدمين...' : 'Search users...'}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <span className="text-muted" style={{ fontSize: 'var(--fs-sm)' }}>
            {isRtl ? `${filteredUsers.length} مستخدم` : `${filteredUsers.length} users`}
          </span>
        </div>
        <div className="card-body p-0">
          <Table
            columns={columns}
            data={filteredUsers}
            emptyMessage={isRtl ? 'لا يوجد مستخدمون' : 'No users found'}
          />
        </div>
      </div>

      {editingUser && (
        <EditUserModal
          user={editingUser}
          isOpen={!!editingUser}
          onClose={() => setEditingUser(null)}
          onSuccess={() => { setEditingUser(null); loadUsers(); }}
        />
      )}
    </div>
  );
};

export default UsersIndex;
